import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Truck, Clock, Package, PackageCheck } from "lucide-react";
import { api } from "../services/api";
import { toast } from "../hooks/use-toast";
import Logo from "../components/Logo";

const fmt = (n) => new Intl.NumberFormat("fr-DZ").format(Math.round(n || 0));
const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }); } catch { return iso; } };

const STEP_ICON = { "Envoyé au fournisseur": Truck, "Reçu": Package, "Préparé": PackageCheck, "Livré": Check };

const SuiviBC = () => {
  const { bid } = useParams();
  const [bc, setBc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = () => api.getPublicBC(bid).then((d) => setBc(d)).catch(() => setBc(false)).finally(() => setLoading(false));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bid]);

  const confirm = async (status) => {
    setSaving(true);
    try {
      await api.confirmBC(bid, status);
      setBc((b) => ({ ...b, status }));
      toast({ title: "Merci ✔", description: `Statut mis à jour : ${status}` });
    } catch {
      toast({ title: "Erreur", description: "Mise à jour impossible." });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-navy font-display">Chargement…</div>;
  if (!bc) return <div className="min-h-screen flex items-center justify-center text-gray-500 font-display">Bon de commande introuvable.</div>;

  const statuses = bc.statuses || ["Envoyé au fournisseur", "Reçu", "Préparé", "Livré"];
  const currentIdx = statuses.indexOf(bc.status);
  const late = new Date(bc.deliveryDeadline) < new Date() && bc.status !== "Livré";

  return (
    <div className="min-h-screen bg-cream py-10 px-5" data-testid="suivi-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex justify-center mb-6"><Logo /></div>
        <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[11px] font-display font-bold text-orange uppercase tracking-wider">Bon de commande</div>
              <h1 className="font-display font-black text-2xl text-navy">BC-{String(bid).slice(0, 6).toUpperCase()}</h1>
            </div>
            <span data-testid="suivi-status" className={`inline-flex items-center gap-1 text-sm font-semibold px-3 py-1.5 rounded-full ${bc.status === "Livré" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700"}`}>{bc.status}</span>
          </div>

          <div className="mt-4 text-sm"><span className="text-gray-500">Fournisseur : </span><span className="font-display font-semibold text-navy">{bc.supplier}</span></div>

          {bc.customer && bc.customer.company && (
            <div className="mt-3 text-sm bg-cream rounded-xl p-3 leading-relaxed">
              <div className="font-display font-bold text-navy">Livrer à : {bc.customer.company} <span className="font-normal text-gray-500">({bc.customer.activity})</span></div>
              <div className="text-gray-600">{bc.customer.contact} · {bc.customer.phone}</div>
              <div className="text-gray-600">📍 {bc.customer.address} — {bc.customer.wilaya}</div>
            </div>
          )}

          <div className="mt-4 border-t border-gray-100 pt-3 space-y-1">
            {(bc.items || []).map((it, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-gray-700">{it.qty} × {it.name}{it.freeQty ? ` (dont ${it.freeQty} offert)` : ""}</span>
                <span className="font-semibold text-navy">{fmt(it.lineHT)} DA</span>
              </div>
            ))}
            <div className="flex justify-between text-sm pt-1 border-t border-gray-100 mt-1"><span className="font-display font-bold text-navy">Total HT</span><span className="font-display font-black text-navy">{fmt(bc.totalHT)} DA</span></div>
          </div>

          <div className={`mt-3 text-xs font-semibold ${late ? "text-red-600" : "text-gray-500"}`}><Clock size={12} className="inline mr-1" /> Livraison avant le {fmtDate(bc.deliveryDeadline)}{late ? " (en retard)" : " (48h)"}</div>

          {/* Progress steps */}
          <div className="mt-6 grid grid-cols-4 gap-2">
            {statuses.map((s, i) => {
              const Icon = STEP_ICON[s] || Truck;
              const active = i <= currentIdx;
              return (
                <div key={s} className="text-center">
                  <div className={`mx-auto w-10 h-10 rounded-full flex items-center justify-center ${active ? "bg-orange text-white" : "bg-gray-100 text-gray-400"}`}><Icon size={18} /></div>
                  <div className={`mt-1 text-[11px] font-display font-semibold ${active ? "text-navy" : "text-gray-400"}`}>{s}</div>
                </div>
              );
            })}
          </div>

          {/* Supplier confirmation */}
          <div className="mt-6">
            <p className="font-display font-semibold text-gray-500 text-xs uppercase tracking-wider mb-2">Confirmez l'avancement</p>
            <div className="flex flex-wrap gap-2">
              {statuses.slice(1).map((s) => (
                <button key={s} onClick={() => confirm(s)} disabled={saving || bc.status === s} data-testid={`confirm-${s}`}
                  className={`font-display font-semibold text-sm px-4 py-2.5 rounded-full border transition-colors disabled:opacity-40 ${bc.status === s ? "bg-navy text-white border-navy" : "bg-white text-navy border-gray-200 hover:border-orange"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          {bc.statusHistory && bc.statusHistory.length > 0 && (
            <div className="mt-6 border-t border-gray-100 pt-3">
              <p className="font-display font-semibold text-gray-500 text-xs uppercase tracking-wider mb-2">Historique</p>
              <ul className="space-y-1" data-testid="status-history">
                {bc.statusHistory.map((h, i) => (
                  <li key={i} className="text-xs text-gray-600 flex justify-between gap-3">
                    <span className="font-semibold text-navy">{h.status}</span>
                    <span className="text-gray-400">{fmtDate(h.at)} · {h.by}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <p className="text-center text-gray-400 text-xs mt-4 font-display">Ssuq | سوق — Suivi fournisseur</p>
      </div>
    </div>
  );
};

export default SuiviBC;
